import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { BaseService } from './base.service';

export interface RankedTask {
  rank: number;
  task_id: string;
  title: string;
  meeting_name: string;
  event_date?: string;
  hours_until_dinner?: number;
  final_score?: number;
  one_sentence_rationale?: string;
  company_id?: string;
  meeting_id?: string;
  checklist_id?: string;
  due_at?: string;
}

export interface TaskPrioritiesAiStatus {
  configured: boolean;
  enabled: boolean;
  max_tasks: number;
  task_count?: number | null;
  tasks_sent_to_ai?: number | null;
  partial?: boolean;
  attempted: boolean;
  reason: string | null;
  error: string | null;
}

export interface TaskPrioritiesResponse {
  task_count: number;
  ranked_tasks: RankedTask[];
  global_insight?: string;
  suggested_batches?: string[];
  source?: 'ai' | 'fallback' | 'none';
  cached?: boolean;
  ai_status?: TaskPrioritiesAiStatus;
}

export const PRIORITIES_CACHE_TTL_MS = 15 * 60 * 1000;

interface PrioritiesCacheEntry {
  staffId: string;
  savedAt: number;
  ranking: TaskPrioritiesResponse;
}

@Injectable()
export class StaffChecklistService extends BaseService {

  constructor(
    protected http: HttpClient,
  ) {
    super(http, `${environment.apiBaseUrl}/staff_checklists`);
  }

  priorities(urlParams: any = {}, apiParams: any = {}): Observable<TaskPrioritiesResponse> {
    const url = `${environment.apiBaseUrl}/staff_checklists/priorities`;
    return this.http.get<TaskPrioritiesResponse>(url, {
      params: this.toHttpParams({ ...urlParams, ...apiParams }),
    });
  }

  readPrioritiesCache(staffId: string): TaskPrioritiesResponse | null {
    if (!staffId) {
      return null;
    }
    try {
      const raw = localStorage.getItem(this.prioritiesCacheKey(staffId));
      if (!raw) {
        return null;
      }
      const entry = JSON.parse(raw) as PrioritiesCacheEntry;
      if (entry.staffId !== staffId || !entry.ranking) {
        return null;
      }
      if (Date.now() - entry.savedAt > PRIORITIES_CACHE_TTL_MS) {
        return null;
      }
      return { ...entry.ranking, cached: true };
    } catch {
      return null;
    }
  }

  writePrioritiesCache(staffId: string, ranking: TaskPrioritiesResponse): void {
    if (!staffId || !ranking) {
      return;
    }
    try {
      const entry: PrioritiesCacheEntry = {
        staffId,
        savedAt: Date.now(),
        ranking,
      };
      localStorage.setItem(this.prioritiesCacheKey(staffId), JSON.stringify(entry));
    } catch {
      // ignore quota / private mode errors
    }
  }

  private prioritiesCacheKey(staffId: string): string {
    return `omni_dashboard_priorities_${staffId}`;
  }
}
